import React, { useMemo } from 'react';
import { buildUiHelpers } from './sharedUi';

const strategySections = [
  {
    title: '📚 Zusammenfassung der RSL-Strategie',
    text: 'Die RSL-Strategie (Relative Stärke nach Robert Levy) ist ein systematisches, trendfolgendes Aktienauswahlverfahren. Für Werte mit hoher RSL-Kennzahl basierend auf den vergangenen 26 Wochen wird eine Fortsetzung des Aufwärtstrends erwartet („The trend is your friend“).',
  },
  {
    title: '🏁 Ziel',
    text: 'Überdurchschnittliche Performance mit Einzelwerten im Vergleich zu Buy-and-Hold des übergeordneten Index. Einstieg und Ausstieg erfolgen ohne Emotionen, Market-Timing oder aufwendige Chart-Analysen, da streng nach Regelwerk gehandelt wird.',
  },
  {
    title: '🟢 Einstieg',
    text: 'Einen Index mit einer ausreichenden Anzahl von Einzeltiteln (z. B. S&P 500, HDAX 100) auswählen und für jede Aktie sowie den Index den RSL-Wert berechnen. Eingestiegen wird in die Aktien mit dem höchsten RSL-Wert, sofern der RSL-Wert der Aktie über dem RSL-Wert des Index liegt und der 5-Wochen-MA der Aktie oberhalb des 13-Wochen-MA liegt. Zur Risikostreuung bietet es sich an, in mehrere Aktien zu gleichen Anteilen einzusteigen. Sollten mehrere Top-Aktien aus der gleichen Branche stammen, sollte vor Einstieg ein mögliches Klumpenrisiko bedacht werden.\n\nIm Backtest über die Vergangenheit war die folgende maximale Anzahl gleichzeitiger Positionen im Depot ein guter Anhaltspunkt:\n\n• S&P 500: 4-5 Aktien\n• Nasdaq 100: 4 Aktien\n• HDAX 100: 5-7 Aktien\n• STOXX 600: 4-5 Aktien',
  },
  {
    title: '🔴 Ausstieg',
    text: 'Eine Aktie entfällt aus dem Depot, wenn ihr RSL-Wert unter 1 sinkt oder\n\n• S&P 500: die Aktie aus den 25 % der Top-Werte des übergeordneten S&P-Index herausfällt\n• Nasdaq 100: die Aktie aus den 22 % der Top-Werte des übergeordneten Nasdaq-Index herausfällt\n• HDAX 100: der RSL-Wert der Aktie unter den RSL-Wert des übergeordneten HDAX-Index sinkt\n• STOXX 600: der RSL-Wert der Aktie unter den RSL-Wert des übergeordneten STOXX-Index herausfällt\n\nEine entfallene Aktie wird zeitnah durch einen neuen Titel der RSL-Rangliste ersetzt. Änderungen im Depot werden nur montags vorgenommen, auch wenn ein Ausstieg bereits in den Tagen davor angezeigt wird.',
  },
];

const appGuideSteps = [
  {
    title: 'Aktien mit RSL-Signal finden',
    bullets: [
      { before: 'Wechsele in den 🔍 ', label: 'Scanner', target: 'screener', after: '.' },
      'Wähle den Index aus, aus dem du die Top-Aktien sehen möchtest.',
      'Prüfe die vorgeschlagenen Werte auf Eignung für dein Depot; über den Chart-Button kannst du dir den Kursverlauf einblenden lassen.',
      'Füge die gewünschten Aktien über den Hinzufügen-Button deinem virtuellen Depot hinzu; in der Literatur hat sich gezeigt, dass 5 bis 7 verschiedene Aktien für die Strategie optimal zu sein scheinen (darüber hinaus verschlechtert sich das Ergebnis in den Backtests).',
    ],
    note: 'Aktien im virtuellen Depot werden direkt unter der Liste mit den Top-Signalen angezeigt; so hast du beim Scannen den Bestand in deinem Depot immer im Blick.',
  },
  {
    title: 'Virtuelles Depot verwalten',
    bullets: [
      { before: 'Wechsele ins virtuelle 💼 ', label: 'Depot', target: 'depot', after: '.' },
      'Prüfe anhand der Signal-Spalte für jede Aktie, ob Handlungsbedarf für die Position besteht.',
      'Bearbeite über den Aktion-Button die Details je Aktienposition: Kaufdatum, Menge, Preis, Nachkauf, Teilverkauf oder Verkauf.',
    ],
    note: 'Füge über das Eingabefeld bei Bedarf weitere Aktien manuell hinzu, falls du Positionen im Depot haben möchtest, die nicht oder nicht mehr im Scanner angezeigt werden.',
  },
  {
    title: 'Handelsverlauf auswerten',
    bullets: [
      { before: 'Wechsele ins 📘 ', label: 'Journal', target: 'rsl_journal', after: '.' },
      'Wähle den Index aus, aus dem du die Entwicklung der Aktien sehen möchtest.',
      'Filtere die Auswertung auf den gewünschten Zeitraum.',
    ],
    note: 'Unterhalb der Grafik findest du eine Übersicht je Index inklusive komplettem Handelsprotokoll.',
  },
  {
    title: 'Strategie-Erfolg in der Vergangenheit testen',
    bullets: [
      { before: 'Wechsele in den 📊 ', label: 'Backtest', target: 'rsl_backtest', after: '.' },
      'Definiere die Parameter, mit denen du testen möchtest, und starte den Backtest.',
      'Die Auswertung zeigt dir, wie sich die Strategie mit den gewählten Parametern in der Vergangenheit entwickelt hätte.',
    ],
    note: 'Das Startdatum wird automatisch auf den Montag der gewählten Woche gesetzt; beim Enddatum wird die letzte verfügbare Wochenkerze bis zu diesem Datum verwendet.',
  },
  {
    title: 'E-Mail-Benachrichtigung verwalten',
    bullets: [
      { before: 'Wechsele ins 👤 ', label: 'Profil', target: 'konto', after: ' im Menüpunkt „…Mehr“.' },
      'Aktiviere oder deaktiviere die E-Mail-Benachrichtigung für dein virtuelles Depot, mit der du Signale direkt in deine Inbox erhältst.',
    ],
    note: 'Im Standard erhältst du die E-Mail-Benachrichtigung jeden Montag um 08:30 Uhr; so kannst du bei Bedarf entspannt zu Börseneröffnung reagieren.',
  },
];

const rslSourceLinks = [
  {
    label: 'RSL (Relative Strength Levy)',
    source: 'https://traderfox.com',
    href: 'https://web.archive.org/web/20260505121629/https://traderfox.com/wissen/indikatoren/rsl-relative-strength-levy/p-413',
  },
  {
    label: 'Relative-Stärke-Strategie',
    source: 'https://aktien-mit-strategie.de',
    href: 'https://web.archive.org/web/20260505121312/https://aktien-mit-strategie.de/relative-staerke-strategie/',
  },
  {
    label: 'Modifizierte Relative-Stärke-Strategie',
    source: 'https://aktien-mit-strategie.de',
    href: 'https://web.archive.org/web/20260505121248/https://aktien-mit-strategie.de/modifizierte-relative-staerke-strategie/',
  },
  {
    label: 'Verbesserung des Konzeptes der Relativen Stärke durch eine dynamische Komponente bzw. eine Relative Stärke/Ratio-Chart-Matrix',
    source: 'https://www.vtad.de',
    href: 'https://web.archive.org/web/20260515135507/https:/www.vtad.de/fa/verbesserung-des-konzeptes-der-relativen-staerke-durch-eine/',
  },
];

export default function CoachingSection({ COLORS, onNavigate }) {
  const { Card } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);
  const sectionStyle = {
    padding: '16px 0',
    borderTop: `1px solid ${COLORS.border}`,
  };
  const stepNumberStyle = {
    width: '30px',
    height: '30px',
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: COLORS.accent,
    color: '#fff',
    fontWeight: '900',
    flex: '0 0 auto',
  };
  const guideLinkStyle = {
    border: 0,
    padding: 0,
    margin: 0,
    background: 'transparent',
    color: COLORS.cyan,
    cursor: 'pointer',
    font: 'inherit',
    fontWeight: '800',
    textDecoration: 'none',
  };
  const externalLinkStyle = {
    color: COLORS.cyan,
    fontWeight: '800',
    textDecoration: 'none',
  };
  const renderGuideBullet = item => {
    if (typeof item === 'string') return item;
    return (
      <>
        {item.before}
        <button type="button" onClick={() => onNavigate?.(item.target)} style={guideLinkStyle}>
          {item.label}
        </button>
        {item.after}
      </>
    );
  };
  const importantBulletStyle = {
    marginBottom: '10px',
  };

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      <Card>
        <div style={{ maxWidth: '960px' }}>
          <div style={{ fontSize: '12px', color: COLORS.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '800' }}>
            Anleitung
          </div>
          <h2 style={{ margin: '0 0 10px', color: COLORS.text, fontSize: '26px', lineHeight: 1.2 }}>
            RSL verstehen und in der App anwenden
          </h2>
          <p style={{ margin: 0, color: COLORS.muted, fontSize: '15px', lineHeight: 1.7 }}>
            Diese Anleitung fasst die RSL-Strategie zusammen und zeigt, wie du Scanner, Depot, Journal, Backtest und Profil sinnvoll im Ablauf nutzt.
          </p>
        </div>
      </Card>

      <Card>
        <h3 style={{ margin: '0 0 4px', color: COLORS.text, fontSize: '18px' }}>RSL-Strategie</h3>
        <div style={{ color: COLORS.muted, fontSize: '13px', marginBottom: '8px' }}>
          Systematisch handeln statt aus dem Bauch heraus entscheiden.
        </div>
        {strategySections.map((section, index) => (
          <section key={section.title} style={index === 0 ? { padding: '10px 0 16px' } : sectionStyle}>
            <h4 style={{ margin: '0 0 8px', color: COLORS.text, fontSize: '15px' }}>{section.title}</h4>
            <p style={{ margin: 0, color: COLORS.muted, fontSize: '14px', lineHeight: 1.75, whiteSpace: 'pre-line' }}>
              {section.text}
            </p>
          </section>
        ))}
      </Card>

      <Card>
        <h3 style={{ margin: '0 0 4px', color: COLORS.text, fontSize: '18px' }}>⚙️ Benutzung der Einfach-Trading App</h3>
        <div style={{ color: COLORS.muted, fontSize: '13px', marginBottom: '10px' }}>
          Der praktische Ablauf vom Signal bis zur Auswertung.
        </div>
        <div style={{ display: 'grid', gap: '14px' }}>
          {appGuideSteps.map((step, index) => (
            <section key={step.title} style={{ display: 'grid', gridTemplateColumns: '30px minmax(0, 1fr)', gap: '12px', paddingTop: index === 0 ? '8px' : '14px', borderTop: index === 0 ? 'none' : `1px solid ${COLORS.border}` }}>
              <div style={stepNumberStyle}>{index + 1}</div>
              <div>
                <h4 style={{ margin: '4px 0 10px', color: COLORS.text, fontSize: '15px' }}>{step.title}</h4>
                <ul style={{ margin: '0 0 10px', paddingLeft: '18px', color: COLORS.muted, fontSize: '14px', lineHeight: 1.75 }}>
                  {step.bullets.map(item => (
                    <li key={typeof item === 'string' ? item : `${item.target}-${item.label}`}>{renderGuideBullet(item)}</li>
                  ))}
                </ul>
                <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.6, fontStyle: 'italic', background: '#ffffff06', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '10px 12px' }}>
                  ℹ {step.note}
                </div>
              </div>
            </section>
          ))}
        </div>
      </Card>

      <Card>
        <h3 style={{ margin: '0 0 14px', color: COLORS.text, fontSize: '18px' }}>💡 Was sonst noch wichtig ist</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: COLORS.muted, fontSize: '14px', lineHeight: 1.75 }}>
          <li style={importantBulletStyle}>
            Nutze die E-Mail-Benachrichtigung! So reicht am Montagmorgen ein Blick in die Inbox zur stressfreien Umsetzung der RSL-Strategie. Das ist eines der Haupt-Features der App.
          </li>
          <li style={importantBulletStyle}>
            Anmerkungen zur RSL-Strategie:
            <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
              <li style={{ marginBottom: '8px' }}>
                Ereignisse: Vor einer Investition sollten Aktien auf bekannte Ereignisse oder News in der jüngeren Vergangenheit oder nahen Zukunft hin überprüft werden. Insbesondere Firmenübernahmen oder -verkäufe, Merger oder ungewöhnliche Marktbewegungen können ein Zeichen sein, im Rahmen der RSL-Strategie nicht in die Aktie zu investieren.
              </li>
              <li>
                Cash-Komponente: Optional kann zur Risikoreduktion gegen längere Abwärtsphasen des Gesamtmarktes eine Cash-Komponente eingesetzt werden. Liegt der über drei Wochen geglättete Durchschnitt des Index-RSL unter 1, wird komplett in Cash gegangen (d. h. Verkauf aller Werte im Depot), da von einem breiten Abwärtstrend ausgegangen wird (Index-RSL &lt; 1,00).
              </li>
            </ul>
          </li>
          <li style={importantBulletStyle}>
            Die Berechnungen in unserer App zur RSL-Strategie basieren auf etablierten Ansätzen. Zur Verbesserung der Trefferquote für den Einstieg setzen wir auf eine "modifizierte Berechnung" des RSL-Signals unter Berücksichtigung gleitenden Durchschnitte. Wer sich für die Grundlagen dahinter interessiert, findet hier die entsprechenden Informationen:
            <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
              {rslSourceLinks.map(link => (
                <li key={link.label} style={{ marginBottom: '8px' }}>
                  <a href={link.href} target="_blank" rel="noopener noreferrer" style={externalLinkStyle}>
                    {link.label}
                  </a>
                  <div style={{ color: COLORS.muted }}>Quelle: {link.source}</div>
                </li>
              ))}
            </ul>
          </li>
          <li style={importantBulletStyle}>
            Tritt unserer Telegram-Community bei für Austausch mit Mitgliedern und Moderatoren:{' '}
            <a href="https://t.me/einfach_trading_de" target="_blank" rel="noopener noreferrer" style={externalLinkStyle}>
              https://t.me/einfach_trading_de
            </a>
          </li>
          <li style={{ marginBottom: 0 }}>
            Für Fragen, Probleme, technische Unterstützung und Anregungen nutze unseren{' '}
            <button type="button" onClick={() => onNavigate?.('support')} style={guideLinkStyle}>
              Support
            </button>{' '}
            im Menüpunkt „…Mehr“.
          </li>
        </ul>
      </Card>
    </div>
  );
}
